import { useAtomValue } from 'jotai'
import { TriangleIcon } from 'lucide-react'
import { memo, useMemo, useRef } from 'react'

import {
  CALL_COLUMN_ORDER,
  OptionTableColumn,
  PUT_COLUMN_ORDER,
} from '@/features/chart/lib/configs/option-setting-config'
import { OptionRowType, ParsedSymbol } from '@/features/chart/lib/configs/options-config'
import { summarizeOptionPositions } from '@/features/chart/lib/option-position-display'
import { formatOptionIv, resolveOptionSymbol } from '@/features/chart/lib/utils/option-util'
import { useCanTradeExchange } from '@/hooks/use-can-trade-exchange'
import { useLinkingGroupSelectedTicker } from '@/hooks/use-linking-group-state'
import { useLongPressContextMenu } from '@/hooks/use-long-press-context-menu'
import { useNumberFormat } from '@/hooks/use-number-format'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { useOrderFormPopup } from '@/hooks/use-order-form-popup'
import { useOrderFormSelectionTarget } from '@/hooks/use-order-form-selection-target'
import { cn } from '@/lib/utils'
import { useInstrumentList } from '@/service/instrument.hook'
import { optionTableConfigAtom } from '@/state/chart'
import { SelectedOption } from '@/types/dashboard'
import { OrderSide, Position } from '@/types/generated/common/trading'
import { Exchange, IncrementalTicker } from '@/types/generated/common/types'

// Helpers
const formatNumber = (val: number | undefined, decimals: number = 2) => {
  if (val === undefined || val === null) return '-'
  return val.toFixed(decimals)
}

const cellClass =
  'col-span-1 px-2 text-foreground text-xs w-full flex items-center justify-center py-1.5 text-xs tracking-tight font-ibm-plex-sans tabular-nums'

const secondaryTextClass = 'text-foreground/60'
const ivTextClass = 'text-[8px] leading-none text-foreground/70'

// Cell renderer helper
const CellContent = ({
  data,
  columnKey,
  handleAskClick,
  handleBidClick,
  isAskSelected,
  isBidSelected,
  positionData,
  accountLabelById,
  contractSize,
  hasSellOrder,
  hasBuyOrder,
}: {
  data?: IncrementalTicker
  columnKey: keyof OptionTableColumn
  handleAskClick: (e: React.MouseEvent) => void
  handleBidClick: (e: React.MouseEvent) => void
  isAskSelected: boolean
  isBidSelected: boolean
  positionData?: Position[]
  accountLabelById?: ReadonlyMap<string, string>
  contractSize?: number
  hasSellOrder?: boolean
  hasBuyOrder?: boolean
}) => {
  const fmt = useNumberFormat()
  const baseCellClass = cn([cellClass])

  switch (columnKey) {
    case 'oi':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.openInterest ?? 0), 2))}
          </span>
        </div>
      )
    case 'delta':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.greeks?.delta ?? 0), 4))}</span>
        </div>
      )
    case 'gamma':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.greeks?.gamma ?? 0), 6))}</span>
        </div>
      )
    case 'theta':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.greeks?.theta ?? 0), 2))}</span>
        </div>
      )
    case 'vega':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.greeks?.vega ?? 0), 2))}</span>
        </div>
      )
    case 'rho':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.greeks?.rho ?? 0), 2))}</span>
        </div>
      )
    case 'mark':
      return (
        <div className={baseCellClass}>
          <div className="flex flex-col items-center">
            <span className="text-foreground">
              {(() => {
                const v = Number(data?.markPrice ?? 0)
                return fmt.text(formatNumber(v, v >= 10 ? 2 : 4))
              })()}
            </span>
            <span className={ivTextClass}>{fmt.text(formatOptionIv(data?.markIv))}</span>
          </div>
        </div>
      )
    case 'lastPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.lastPrice ?? 0), 4))}
          </span>
        </div>
      )
    case 'indexPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.indexPrice ?? 0), 2))}
          </span>
        </div>
      )
    case 'settlementPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.settlementPrice ?? 0), 2))}
          </span>
        </div>
      )
    case 'underlyingPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.underlyingPrice ?? 0), 2))}
          </span>
        </div>
      )
    case 'minPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.minPrice ?? 0), 4))}
          </span>
        </div>
      )
    case 'maxPrice':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.maxPrice ?? 0), 4))}
          </span>
        </div>
      )
    case 'high24h':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.stats?.high ?? 0), 4))}
          </span>
        </div>
      )
    case 'low24h':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.stats?.low ?? 0), 4))}
          </span>
        </div>
      )
    case 'priceChange24h':
      return (
        <div className={baseCellClass}>
          <span
            className={
              Number(data?.stats?.priceChangePercent ?? 0) >= 0
                ? 'text-chart-mint'
                : 'text-chart-red'
            }
          >
            {fmt.text(formatNumber(Number(data?.stats?.priceChangePercent ?? 0), 2))}%
          </span>
        </div>
      )
    case 'volume':
      return (
        <div className={baseCellClass}>
          <span className={secondaryTextClass}>
            {fmt.text(formatNumber(Number(data?.stats?.volumeBase ?? 0), 2))}
          </span>
        </div>
      )
    case 'sizeAsk':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.bestAskQuantity ?? 0), 2))}</span>
        </div>
      )
    case 'sizeBid':
      return (
        <div className={baseCellClass}>
          <span>{fmt.text(formatNumber(Number(data?.bestBidQuantity ?? 0), 2))}</span>
        </div>
      )
    case 'bid': {
      return (
        <div className={cn([cellClass, 'px-0.2 py-0.2'])}>
          <button
            type="button"
            data-1p-ignore
            onClick={(e) => handleBidClick(e)}
            className={cn([
              'bg-foreground/10 relative flex h-[calc(100%-4px)] w-[calc(100%-4px)] cursor-pointer flex-col items-center justify-center',
              // Hover styling only while unselected: the pointer is still on the button
              // right after a click, and a `hover:bg-*` utility outranks the plain
              // `bg-*` selected color in the cascade — the selected fill would stay
              // washed out until the mouse moved away, reading as a laggy transition.
              // Bid cell = red, ask cell = green.
              isBidSelected ? 'bg-chart-red text-ox-ink' : 'text-chart-red hover:bg-foreground/15',
            ])}
          >
            <div>
              <div className="relative">
                {hasSellOrder && (
                  <TriangleIcon className="text-chart-red fill-chart-red absolute top-1/2 -left-px size-1.5 -translate-x-full -translate-y-[calc(50%+1px)] rotate-90" />
                )}
                {data?.bestBidPrice === undefined
                  ? '-'
                  : Number(data?.bestBidPrice).toLocaleString('en-US', {
                      maximumFractionDigits: 5,
                    })}
              </div>
            </div>
            <span className={cn(ivTextClass, isBidSelected && 'text-ox-ink')}>
              {fmt.text(formatOptionIv(data?.bidIv))}
            </span>
          </button>
        </div>
      )
    }
    case 'ask': {
      return (
        <div className={cn([cellClass, 'px-0.2 py-0.2'])}>
          <button
            type="button"
            data-1p-ignore
            onClick={(e) => handleAskClick(e)}
            className={cn([
              'bg-foreground/10 flex h-[calc(100%-4px)] w-[calc(100%-4px)] cursor-pointer flex-col items-center justify-center',
              // See the bid button above — hover must not survive selection.
              isAskSelected
                ? 'bg-chart-mint text-ox-ink'
                : 'text-chart-mint hover:bg-foreground/15',
            ])}
          >
            <div className="relative">
              {data?.bestAskPrice === undefined
                ? '-'
                : Number(data?.bestAskPrice).toLocaleString('en-US', {
                    maximumFractionDigits: 5,
                  })}
              {hasBuyOrder && (
                <TriangleIcon className="text-chart-mint fill-chart-mint absolute top-1/2 -right-px size-1.5 translate-x-full -translate-y-[calc(50%+1px)] rotate-270" />
              )}
            </div>
            <span className={cn(ivTextClass, isAskSelected && 'text-ox-ink')}>
              {fmt.text(formatOptionIv(data?.askIv))}
            </span>
          </button>
        </div>
      )
    }
    case 'position': {
      if (!positionData?.length) {
        return <div className={cn([cellClass, 'px-0.2 py-0.2'])} />
      }
      const exposure = summarizeOptionPositions(positionData, contractSize)
      const formatSize = (size: number) =>
        size.toLocaleString('en-US', {
          maximumFractionDigits: 4,
          minimumFractionDigits: 0,
        })
      const hasLong = exposure.long > 0
      const hasShort = exposure.short > 0
      const displaySize =
        hasLong && hasShort
          ? `+${formatSize(exposure.long)} / -${formatSize(exposure.short)}`
          : formatSize(hasLong ? exposure.long : exposure.short)
      const tooltip = exposure.details
        .map(({ accountId, side, size }) => {
          const accountName = accountLabelById?.get(accountId)?.trim()
          const fallbackId = accountId.length > 8 ? `${accountId.slice(0, 6)}…` : accountId
          return `${accountName || fallbackId || 'Account'}: ${side === 'long' ? '+' : '-'}${formatSize(size)}`
        })
        .join('\n')

      return (
        <div className={cn([cellClass, 'px-0.2 py-0.2'])}>
          <div
            title={fmt.hidden ? undefined : fmt.text(tooltip)}
            className={cn([
              'bg-foreground/10 flex h-[calc(100%-4px)] w-[calc(100%-4px)] items-center justify-center',
              hasLong && hasShort
                ? 'text-foreground ring-chart-red/50 ring-1 ring-inset'
                : hasLong
                  ? 'text-chart-mint'
                  : 'text-chart-red',
            ])}
          >
            {fmt.text(displaySize, { sensitive: true })}
          </div>
        </div>
      )
    }
    default:
      return null
  }
}

// Module-level so the cardId-less fallback keeps a stable identity across renders
// instead of allocating a fresh closure per cell.
const NOOP_SET_TICKER = () => {}

// Unified Option Side component - renders cells in normal or reversed order
const OptionSide = memo(
  function OptionSide({
    data,
    isLoading,
    reverse = false,
    visibleColumns,
    itmBgColor,
    positionBgColor,
    positionData,
    accountLabelById,
    contractSize,
    minTradeAmount,
    cardId,
    hasSellOrder,
    hasBuyOrder,
    optionSymbolMap,
    exchange,
    onContextMenuRequest,
  }: {
    data?: IncrementalTicker
    isLoading?: boolean
    reverse?: boolean // true for PutSide (reversed column order)
    visibleColumns: OptionTableColumn
    itmBgColor?: string // ITM region background (always shown)
    positionBgColor?: string // position overlay background (shown on top of ITM)
    positionData?: Position[] // Account-preserving position data for this option
    accountLabelById?: ReadonlyMap<string, string>
    contractSize?: number
    minTradeAmount?: string
    cardId?: string
    hasSellOrder?: boolean
    hasBuyOrder?: boolean
    optionSymbolMap?: Map<string, ParsedSymbol>
    exchange: Exchange
    onContextMenuRequest?: (x: number, y: number, symbol: string) => void
  }) {
    // Narrow subscription on purpose: this component is mounted twice per strike, so
    // taking the whole linking-group slice here meant a write to ANY of its fields
    // re-rendered the entire option chain — and the parent OptionRow's memo can't stop
    // an atom subscription inside its own child. See useLinkingGroupField.
    const _linkingTicker = useLinkingGroupSelectedTicker(cardId ?? '')
    const setSelectedTicker = cardId ? _linkingTicker.setSelectedTicker : NOOP_SET_TICKER
    const selectedTicker = cardId ? _linkingTicker.selectedTicker : null
    const { setSelectedOptions } = useOrderFormDispatch(cardId ?? '')
    const { selectedOptions } = useOrderFormSelectionTarget(cardId ?? '')
    const { openOrderFormPopup } = useOrderFormPopup()
    const canTrade = useCanTradeExchange()
    const containerRef = useRef<HTMLDivElement>(null)
    const longPress = useLongPressContextMenu()
    const exchangeValue = exchange.toString()
    const isSameExchangeOption = (option: SelectedOption) =>
      (option.exchange ?? exchangeValue) === exchangeValue

    // Check if current option's ask/bid is selected
    const isAskSelected = selectedOptions.some(
      (opt) =>
        opt.instrumentName === data?.symbol && opt.side === 'ask' && isSameExchangeOption(opt)
    )
    const isBidSelected = selectedOptions.some(
      (opt) =>
        opt.instrumentName === data?.symbol && opt.side === 'bid' && isSameExchangeOption(opt)
    )

    // Update selectedTicker so the orderbook subscribes to this option's symbol
    const updateSelectedTicker = (symbol: string) => {
      const parsed = resolveOptionSymbol(symbol, optionSymbolMap ?? new Map())
      if (parsed) {
        setSelectedTicker({
          symbol,
          type: 'options',
          baseCurrency: parsed.baseCurrency,
          expiry: parsed.expiry,
        })
      }
    }

    const getAnchor = (e: React.MouseEvent) => {
      const widgetEl = (e.target as HTMLElement).closest('.react-grid-item') ?? containerRef.current
      if (!widgetEl) return null
      const rect = widgetEl.getBoundingClientRect()
      return { right: rect.right, left: rect.left, top: rect.top, bottom: rect.bottom }
    }

    const handleAskClick = (e: React.MouseEvent) => {
      e.stopPropagation()

      if (isAskSelected) {
        // Remove from selection
        setSelectedOptions((prev) =>
          prev.filter(
            (opt) =>
              !(
                opt.instrumentName === data?.symbol &&
                opt.side === 'ask' &&
                isSameExchangeOption(opt)
              )
          )
        )
      } else {
        if (!canTrade(exchange)) return
        // Add to selection and remove bid if exists (can't select both ask and bid for same instrument)
        // Ask (Green) → long (buy)
        const parsed = data?.symbol
          ? resolveOptionSymbol(data.symbol, optionSymbolMap ?? new Map())
          : null
        const newOption: SelectedOption = {
          instrumentName: data?.symbol ?? '',
          // Generic chart Bid/Ask add — no bound source account, follows the form's own
          // account resolution.
          accountBinding: { kind: 'inherited' },
          exchange: exchangeValue,
          side: 'ask',
          tradeSide: 'buy',
          price: data?.bestAskPrice ?? '0',
          ticker: data,
          minTradeAmount,
          strikePrice: parsed ? String(parsed.strike) : undefined,
          optionType: parsed ? (parsed.type === 'C' ? 'call' : 'put') : undefined,
        }
        setSelectedOptions((prev) => [
          newOption,
          ...prev.filter(
            (opt) =>
              !(
                opt.instrumentName === data?.symbol &&
                opt.side === 'bid' &&
                isSameExchangeOption(opt)
              )
          ),
        ])
        if (cardId) openOrderFormPopup(cardId, getAnchor(e))
      }
    }

    const handleBidClick = (e: React.MouseEvent) => {
      e.stopPropagation()

      if (isBidSelected) {
        // Remove from selection
        setSelectedOptions((prev) =>
          prev.filter(
            (opt) =>
              !(
                opt.instrumentName === data?.symbol &&
                opt.side === 'bid' &&
                isSameExchangeOption(opt)
              )
          )
        )
      } else {
        if (!canTrade(exchange)) return
        // Add to selection and remove ask if exists (can't select both ask and bid for same instrument)
        // Bid (Red) → short (sell)
        const parsed = data?.symbol
          ? resolveOptionSymbol(data.symbol, optionSymbolMap ?? new Map())
          : null
        const newOption: SelectedOption = {
          instrumentName: data?.symbol ?? '',
          // Generic chart Bid/Ask add — no bound source account, follows the form's own
          // account resolution.
          accountBinding: { kind: 'inherited' },
          exchange: exchangeValue,
          side: 'bid',
          tradeSide: 'sell',
          price: data?.bestBidPrice ?? '0',
          ticker: data,
          minTradeAmount,
          strikePrice: parsed ? String(parsed.strike) : undefined,
          optionType: parsed ? (parsed.type === 'C' ? 'call' : 'put') : undefined,
        }
        setSelectedOptions((prev) => [
          newOption,
          ...prev.filter(
            (opt) =>
              !(
                opt.instrumentName === data?.symbol &&
                opt.side === 'ask' &&
                isSameExchangeOption(opt)
              )
          ),
        ])
        if (cardId) openOrderFormPopup(cardId, getAnchor(e))
      }
    }

    // Row-level click only selects the option for linked widgets. Chart navigation is kept
    // separate so inspecting an option does not replace or add a chart tab.
    const isLinked = data?.symbol ? selectedTicker?.symbol === data.symbol : false

    const handleSideClick = (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return
      const symbol = data?.symbol
      if (!symbol) return
      updateSelectedTicker(symbol)
    }

    const handleSideContextMenu = (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return
      const symbol = data?.symbol
      if (!symbol) return
      e.preventDefault()
      onContextMenuRequest?.(e.clientX, e.clientY, symbol)
    }

    // Get visible columns in the correct order
    const columnOrder = reverse ? PUT_COLUMN_ORDER : CALL_COLUMN_ORDER
    const activeColumns = useMemo(
      () => columnOrder.filter((key) => visibleColumns[key]),
      [columnOrder, visibleColumns]
    )

    const colCount = activeColumns.length

    if (isLoading) {
      return (
        <div
          className={cn('grid flex-1', itmBgColor, positionBgColor)}
          style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
        >
          <div
            className="text-foreground/20 flex items-center justify-center"
            style={{ gridColumn: `span ${colCount}` }}
          >
            -----loading------
          </div>
        </div>
      )
    }

    return (
      <div
        ref={containerRef}
        className={cn(
          'grid flex-1 cursor-pointer gap-0.5',
          itmBgColor,
          positionBgColor,
          isLinked && 'ring-1 ring-white/25 ring-inset'
        )}
        style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
        onClick={handleSideClick}
        onContextMenu={handleSideContextMenu}
        {...longPress.contextMenuTouchHandlers}
        onTouchStart={(e) => {
          // Same carve-out as handleSideContextMenu: a press on the bid/ask buttons is theirs.
          if ((e.target as HTMLElement).closest('button')) return
          longPress.contextMenuTouchHandlers.onTouchStart(e)
        }}
      >
        {activeColumns.map((columnKey) => (
          <CellContent
            key={columnKey}
            data={data}
            columnKey={columnKey}
            handleAskClick={handleAskClick}
            handleBidClick={handleBidClick}
            isAskSelected={isAskSelected}
            isBidSelected={isBidSelected}
            positionData={positionData}
            accountLabelById={accountLabelById}
            contractSize={contractSize}
            hasSellOrder={hasSellOrder}
            hasBuyOrder={hasBuyOrder}
          />
        ))}
      </div>
    )
  },
  (prev, next) => {
    return (
      prev.data === next.data &&
      prev.isLoading === next.isLoading &&
      prev.reverse === next.reverse &&
      prev.visibleColumns === next.visibleColumns &&
      prev.itmBgColor === next.itmBgColor &&
      prev.positionBgColor === next.positionBgColor &&
      prev.positionData === next.positionData &&
      prev.accountLabelById === next.accountLabelById &&
      prev.contractSize === next.contractSize &&
      prev.minTradeAmount === next.minTradeAmount &&
      prev.hasSellOrder === next.hasSellOrder &&
      prev.hasBuyOrder === next.hasBuyOrder &&
      prev.cardId === next.cardId &&
      prev.optionSymbolMap === next.optionSymbolMap &&
      prev.exchange === next.exchange &&
      prev.onContextMenuRequest === next.onContextMenuRequest
    )
  }
)

// --- Main Row Component ---

export const OptionRow = memo(
  function OptionRow({
    isCallItm,
    isPutItm,
    strike,
    call,
    put,
    isLoading,
    cardId,
    callPositions,
    putPositions,
    callHasBuyOrder = false,
    callHasSellOrder = false,
    putHasBuyOrder = false,
    putHasSellOrder = false,
    accountLabelById,
    optionSymbolMap,
    exchange,
    onContextMenuRequest,
  }: OptionRowType & {
    cardId?: string
    callPositions?: Position[]
    putPositions?: Position[]
    callHasBuyOrder?: boolean
    callHasSellOrder?: boolean
    putHasBuyOrder?: boolean
    putHasSellOrder?: boolean
    accountLabelById?: ReadonlyMap<string, string>
    optionSymbolMap?: Map<string, ParsedSymbol>
    exchange: Exchange
    onContextMenuRequest?: (x: number, y: number, symbol: string) => void
  }) {
    const config = useAtomValue(optionTableConfigAtom)
    const visibleColumns = config.columns
    const { data: instrumentListData } = useInstrumentList(exchange)
    const contractSizeMap = useMemo(() => {
      const map: Record<string, number> = {}
      instrumentListData?.instruments.forEach((inst) => {
        const cs = Number(inst.contractSize)
        if (cs > 0) map[inst.symbol] = cs
      })
      return map
    }, [instrumentListData])

    const minTradeAmountMap = useMemo(() => {
      const map: Record<string, string> = {}
      instrumentListData?.instruments.forEach((inst) => {
        if (inst.minTradeAmount) map[inst.symbol] = inst.minTradeAmount
      })
      return map
    }, [instrumentListData])

    // Show loading if explicitly loading OR if data is not yet available
    const isCallLoading = isLoading || !call
    const isPutLoading = isLoading || !put

    // ITM background is decided purely by strike vs the underlying, regardless of
    // position — the verdicts arrive pre-computed so a price move that doesn't cross
    // this strike can't invalidate the row (see the comparator below).
    const callItmBg = isCallItm ? 'bg-white/5' : undefined
    const putItmBg = isPutItm ? 'bg-white/5' : undefined

    // Position overlay only applies to rows with a position.
    const callPositionBg = useMemo(() => {
      if (!callPositions?.length) return undefined
      const hasLong = callPositions.some(
        (position) =>
          position.side === OrderSide.ORDER_SIDE_BUY ||
          Number(position.side) === OrderSide.ORDER_SIDE_BUY
      )
      const hasShort = callPositions.some(
        (position) =>
          position.side === OrderSide.ORDER_SIDE_SELL ||
          Number(position.side) === OrderSide.ORDER_SIDE_SELL
      )
      if (hasLong && hasShort) return 'bg-chart-mint/20 ring-1 ring-inset ring-chart-red/40'
      return hasShort ? 'bg-chart-red/15' : 'bg-chart-mint/20'
    }, [callPositions])

    const putPositionBg = useMemo(() => {
      if (!putPositions?.length) return undefined
      const hasLong = putPositions.some(
        (position) =>
          position.side === OrderSide.ORDER_SIDE_BUY ||
          Number(position.side) === OrderSide.ORDER_SIDE_BUY
      )
      const hasShort = putPositions.some(
        (position) =>
          position.side === OrderSide.ORDER_SIDE_SELL ||
          Number(position.side) === OrderSide.ORDER_SIDE_SELL
      )
      if (hasLong && hasShort) return 'bg-chart-mint/20 ring-1 ring-inset ring-chart-red/40'
      return hasShort ? 'bg-chart-red/15' : 'bg-chart-mint/20'
    }, [putPositions])

    return (
      <div
        className="flex min-h-9 gap-0.5 text-[10px]"
        style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 36px' }}
      >
        <OptionSide
          data={call}
          isLoading={isCallLoading}
          visibleColumns={visibleColumns}
          itmBgColor={callItmBg}
          positionBgColor={callPositionBg}
          positionData={callPositions}
          accountLabelById={accountLabelById}
          contractSize={call?.symbol ? contractSizeMap[call.symbol] : undefined}
          minTradeAmount={call?.symbol ? minTradeAmountMap[call.symbol] : undefined}
          cardId={cardId}
          hasSellOrder={callHasSellOrder}
          hasBuyOrder={callHasBuyOrder}
          optionSymbolMap={optionSymbolMap}
          exchange={exchange}
          onContextMenuRequest={onContextMenuRequest}
        />

        {/* --- STRIKE --- */}
        <div className="text-foreground flex w-12 shrink-0 items-center justify-center py-1.5 font-medium tabular-nums">
          {strike}
        </div>

        <OptionSide
          data={put}
          isLoading={isPutLoading}
          reverse
          visibleColumns={visibleColumns}
          itmBgColor={putItmBg}
          positionBgColor={putPositionBg}
          positionData={putPositions}
          accountLabelById={accountLabelById}
          contractSize={put?.symbol ? contractSizeMap[put.symbol] : undefined}
          minTradeAmount={put?.symbol ? minTradeAmountMap[put.symbol] : undefined}
          cardId={cardId}
          hasSellOrder={putHasSellOrder}
          hasBuyOrder={putHasBuyOrder}
          optionSymbolMap={optionSymbolMap}
          exchange={exchange}
          onContextMenuRequest={onContextMenuRequest}
        />
      </div>
    )
  },
  (prev, next) => {
    return (
      prev.isCallItm === next.isCallItm &&
      prev.isPutItm === next.isPutItm &&
      prev.strike === next.strike &&
      prev.call === next.call &&
      prev.put === next.put &&
      prev.isLoading === next.isLoading &&
      prev.callPositions === next.callPositions &&
      prev.putPositions === next.putPositions &&
      prev.callHasBuyOrder === next.callHasBuyOrder &&
      prev.callHasSellOrder === next.callHasSellOrder &&
      prev.putHasBuyOrder === next.putHasBuyOrder &&
      prev.putHasSellOrder === next.putHasSellOrder &&
      prev.accountLabelById === next.accountLabelById &&
      prev.optionSymbolMap === next.optionSymbolMap &&
      prev.exchange === next.exchange &&
      prev.onContextMenuRequest === next.onContextMenuRequest
    )
  }
)
